import { Injectable } from '@angular/core';
import { ActivatedRoute, PRIMARY_OUTLET } from '@angular/router';
import { BreadCrumb } from '../models/common.models';
import { ESeoData, ETitleSources } from '../configurations/seo';
import { ERouteDataTypes } from '../configurations/common';

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbsService {
  ROUTE_DATA_BREADCRUMB: string = 'breadcrumb';
  DYNAMIC_DATA_BREADCRUMB: string = 'dynamicBreadCrumb';
  SHOW_DATA_BREADCRUMB: string = 'showBreadcrumb';
  IS_BREADCRUMB_DISABLED: string = 'breadcrumbDisabled';

  getBreadcrumbsParam(route: ActivatedRoute, param: ESeoData | ERouteDataTypes): ETitleSources | boolean {
    const children: ActivatedRoute[] = route.children;
    let result: ETitleSources | boolean = false;
    if (children.length === 0) {
      return result;
    }
    for (const child of children) {
      // verify primary route
      if (child.outlet !== PRIMARY_OUTLET) {
        continue;
      }
      if (Object.hasOwn(child.snapshot.data, param)) {
        result = child.snapshot.data[param];
      }
    }
    return result;
  }

  getBreadcrumbs(route: ActivatedRoute, url: string = '', breadcrumbs: BreadCrumb[] = []): BreadCrumb[] {
    let isDynamic: boolean = false;
    let breadcrumbDisabled: boolean = false;

    // get the child routes
    const children: ActivatedRoute[] = route.children;

    // return if there are no more children
    if (children.length === 0) {
      return breadcrumbs;
    }

    // iterate over each children
    for (const child of children) {
      // verify primary route
      if (child.outlet !== PRIMARY_OUTLET || child.snapshot.url.length === 0) {
        continue;
      }

      if (Object.hasOwn(child.snapshot.data, this.DYNAMIC_DATA_BREADCRUMB)) {
        isDynamic = child.snapshot.data[this.DYNAMIC_DATA_BREADCRUMB];
      }

      if (Object.hasOwn(child.snapshot.data, this.IS_BREADCRUMB_DISABLED)) {
        breadcrumbDisabled = child.snapshot.data[this.IS_BREADCRUMB_DISABLED];
      }

      // verify the custom data property "breadcrumb" is specified on the route
      if (!Object.hasOwn(child.snapshot.data, this.ROUTE_DATA_BREADCRUMB)) {
        return this.getBreadcrumbs(child, url, breadcrumbs);
      }

      // get the route's URL segment
      const routeURL: string = child.snapshot.url.map((segment: { path: string }) => segment.path).join('/');

      const { label, param }: { label: string; param: string } = this.resolveBreadcrumbLabel(child, isDynamic);

      // append route URL to URL
      url += `/${routeURL}`;

      // add breadcrumb
      const breadcrumb: BreadCrumb = {
        label,
        url,
        param,
        breadcrumbDisabled,
      };
      breadcrumbs.push(breadcrumb);

      // recursive
      return this.getBreadcrumbs(child, url, breadcrumbs);
    }
    return breadcrumbs;
  }

  private resolveBreadcrumbLabel(child: ActivatedRoute, isDynamic: boolean): { label: string; param: string } {
    let label: string = child.snapshot.data[this.ROUTE_DATA_BREADCRUMB];
    let param: string = '';

    if (!isDynamic) {
      return { label, param };
    }

    if (label.includes(':translateKey')) {
      param = 'translateKey';
      label = label.split('=')[1];
    } else {
      const paramToUse: string = child.snapshot.data[this.ROUTE_DATA_BREADCRUMB].replace(':', '');
      param = paramToUse;

      const paramValue: string = child.snapshot.params[paramToUse];
      label = paramValue
        .replace(/-{3}/gi, ' / ')
        .replace(/-/gi, ' ');

      label = label.split(' ').map((labelPart: string): string => {
        const capitalized: string = labelPart.charAt(0).toUpperCase() + labelPart.slice(1);
        return capitalized === 'De' ? capitalized.toLowerCase() : capitalized;
      }).join(' ');
    }

    return { label, param };
  }
}
